/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import { createReducer, type Draft } from '@reduxjs/toolkit';

import { mergeDeep, updateValue } from '../../state-manager';
import * as ActionTypes from '../../actions';
import PegaConstants from '../../../../constants';
import Utils from '../../../../utils/utils';
import {
  processSharedData,
  setCaseViewMode,
  setActionTypeAndAssignmentId,
  deleteBlockingActionCounter
} from '../helper';

interface StateShape {
  [key: string]: any;
}

type TransientItem = string | string[];
type SuggestionContext = string | string[];

interface ContainerItemPayload {
  containerItemID: string;
  context: string;
  deleteContext?: boolean;
}

const deleteTransientItems = (draft: Draft<StateShape>, transientItems: TransientItem[] = []) => {
  transientItems.forEach((transientItem) => Utils.deleteIn(draft, transientItem));
};

const deleteSuggestionContexts = (draft: Draft<StateShape>, suggestionContexts: SuggestionContext[] = []) => {
  suggestionContexts.forEach((suggestionContext) => Utils.deleteIn(draft, suggestionContext));
};

const deleteChildContainersContexts = (draft: Draft<StateShape>, childContainersContexts: string[] = []) => {
  childContainersContexts.forEach((context) => {
    Utils.deleteIn(draft, context);
  });
};

const deleteCaseSummary = (
  draft: Draft<StateShape>,
  context: string,
  options: {
    deleteAssignmentSpecificResources?: boolean;
    doDeleteConfirmationDetails?: boolean;
    skipMergeAssignmentsAndActions?: boolean;
  } = {}
) => {
  const {
    deleteAssignmentSpecificResources = true,
    doDeleteConfirmationDetails = true,
    skipMergeAssignmentsAndActions = true
  } = options;
  Utils.deleteIn(draft, [context, 'nextAssignmentInfo']);
  if (skipMergeAssignmentsAndActions) {
    Utils.deleteIn(draft, [context, PegaConstants.CASE_INFO.CASE_INFO, 'availableActions']);
  }
  Utils.deleteIn(draft, [context, PegaConstants.CASE_INFO.CASE_INFO, 'availableProcesses']);
  Utils.deleteIn(draft, [context, PegaConstants.CASE_INFO.CASE_INFO, 'activeActionID']);
  Utils.deleteIn(draft, [context, PegaConstants.CASE_INFO.CASE_INFO, 'assignments']);
  Utils.deleteIn(draft, [context, PegaConstants.CASE_INFO.CASE_INFO, 'childCases']);
  if (doDeleteConfirmationDetails) {
    Utils.deleteIn(draft, [context, PegaConstants.CASE_INFO.CASE_INFO, 'confirmationLinks']);
    Utils.deleteIn(draft, [context, 'caseMessages']);
  }
  if (deleteAssignmentSpecificResources) {
    Utils.deleteIn(draft, [context, PegaConstants.CASE_INFO.CASE_INFO, 'actionButtons']);
    Utils.deleteIn(draft, [context, PegaConstants.CASE_INFO.CASE_INFO, 'navigation']);
  }
};

const deleteContainerItemContext = (draft: Draft<StateShape>, payload: ContainerItemPayload) => {
  const { containerItemID, context, deleteContext } = payload;
  if (deleteContext) {
    Utils.deleteIn(draft, [context]);
  }
  Utils.deleteIn(draft, [containerItemID]);
};

const initialState: StateShape = {};

const caseReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(ActionTypes.ADD_CONTAINER_ITEM, (state, action: any) => {
      if (!action.payload.data || state[action.payload.context]) {
        return;
      }

      if (action.payload.doDeleteCaseSummary) {
        deleteCaseSummary(state, action.payload.context);
      }

      const { target } = action.payload;
      if (target && (target.indexOf(PegaConstants.WORKAREA) !== -1 || target.indexOf(PegaConstants.MODAL) !== -1)) {
        state[action.payload.context] = action.payload.data;
      } else {
        state[action.payload.containerItemID] = action.payload.data;
      }

      setCaseViewMode(state, action.payload);
      setActionTypeAndAssignmentId(state, action.payload);
      processSharedData(state, action.payload);
      deleteBlockingActionCounter(state, action.payload);
    })
    .addCase(ActionTypes.UPDATE_CONTAINER_ITEM, (state, action: any) => {
      if (action.payload.doDeleteCaseSummary) {
        deleteCaseSummary(state, action.payload.context, {
          deleteAssignmentSpecificResources: action.payload.deleteAssignmentSpecificResources
        });
      }

      const { target, propertiesToReplace = [] } = action.payload;

      if (target && (target.indexOf(PegaConstants.WORKAREA) !== -1 || target.indexOf(PegaConstants.MODAL) !== -1)) {
        state[action.payload.context] = mergeDeep(state[action.payload.context], action.payload.data, {
          propertiesToReplace: [...propertiesToReplace]
        });
      } else {
        state[action.payload.containerItemID] = mergeDeep(state[action.payload.containerItemID], action.payload.data, {
          propertiesToReplace: [...propertiesToReplace]
        });
      }

      if (action.payload.actionType || action.payload.assignmentID) {
        setActionTypeAndAssignmentId(state, action.payload);
      }
      setCaseViewMode(state, action.payload);
      processSharedData(state, action.payload);
      deleteSuggestionContexts(state, action.payload.suggestionContexts);
    })
    .addCase(ActionTypes.REMOVE_CONTAINER_ITEM, (state, action: any) => {
      const { childContainersContexts = [] } = action.payload;
      deleteContainerItemContext(state, action.payload);
      deleteChildContainersContexts(state, childContainersContexts);
      deleteTransientItems(state, action.payload.transientItems);
      deleteSuggestionContexts(state, action.payload.suggestionContexts);
    })
    .addCase(ActionTypes.RESET_CONTAINERS, (state, action: any) => {
      const { containerItems } = action.payload;
      if (Array.isArray(containerItems)) {
        containerItems.forEach((containerItem) => {
          Utils.deleteIn(state, [containerItem]);
        });
      } else {
        Utils.deleteIn(state, [containerItems]);
      }
    })
    .addCase(ActionTypes.UPMERGE_AND_REMOVE_CONTAINER_ITEM, (state, action: any) => {
      const { context, contextToBeRemoved, data, childContainersContextsToBeRemoved } = action.payload;
      deleteCaseSummary(state, action.payload.context);
      setCaseViewMode(state, action.payload);
      state[context] = mergeDeep(state[context], state[contextToBeRemoved]);
      state[context] = mergeDeep(state[context], data);
      Utils.deleteIn(state, [contextToBeRemoved]);
      deleteChildContainersContexts(state, childContainersContextsToBeRemoved);
      processSharedData(state, action.payload);
    })
    .addCase(ActionTypes.REPLACE_CONTAINER_ITEM, (state, action: any) => {
      const { context, pulseReplace = true, childContainersContexts = [] } = action.payload;
      let { data } = action.payload;

      const pulse = Utils.getIn(state[context], 'pulse');
      let OperatorID = Utils.getIn(state[context], 'OperatorID');
      let pxRequestor = Utils.getIn(state[context], 'pxRequestor');

      if (pxRequestor && data.pxRequestor) pxRequestor = Utils.mergeDeep(pxRequestor, data.pxRequestor);
      if (OperatorID && data.OperatorID) OperatorID = Utils.mergeDeep(OperatorID, data.OperatorID);

      if (!pulseReplace && pulse) {
        data = updateValue(data, 'pulse', pulse);
        data = updateValue(data, 'OperatorID', OperatorID);
        data = updateValue(data, 'pxRequestor', pxRequestor);
      }
      Utils.setIn(state, context, data);
      setCaseViewMode(state, action.payload);
      processSharedData(state, action.payload);
      deleteChildContainersContexts(state, childContainersContexts);
      deleteTransientItems(state, action.payload.transientItems);
      deleteSuggestionContexts(state, action.payload.suggestionContexts);
    })
    .addCase(ActionTypes.DELETE_CASE_SUMMARY, (state, action: any) => {
      const { context, doDeleteConfirmationDetails = true, skipMergeAssignmentsAndActions = true } = action.payload;
      deleteCaseSummary(state, context, { doDeleteConfirmationDetails, skipMergeAssignmentsAndActions });
    })
    .addCase(ActionTypes.ADD_TRANSIENT_ITEM, (state, action: any) => {
      const { transientItemID, data } = action.payload;
      Utils.setIn(state, transientItemID, data);
    })
    .addCase(ActionTypes.REMOVE_TRANSIENT_ITEM, (state, action: any) => {
      const { transientItems } = action.payload;
      transientItems.forEach((transientItem: any) => Utils.deleteIn(state, transientItem));
    })
    .addCase(ActionTypes.REMOVE_ALL_TRANSIENT_ITEMS, (state, action: any) => {
      const { transientItems } = action.payload;
      transientItems.forEach((transientItem: any) => Utils.deleteIn(state, transientItem));
    })
    .addCase(ActionTypes.UPDATE_TRANSIENT_DATA, (state, action: any) => {
      const { transientItemID, data } = action.payload;
      mergeDeep(Utils.getIn(state, transientItemID), data);
    })
    .addCase(ActionTypes.CLEAR_TRANSIENT_DATA, (state, action: any) => {
      const { transientItemID, data } = action.payload;
      mergeDeep(Utils.getIn(state, transientItemID), data);
    })
    .addCase(ActionTypes.PURGE_TRANSIENT_DATA, (state, action: any) => {
      const { transientItemID, data } = action.payload;
      Object.keys(data ?? {}).forEach((key) => {
        Utils.deleteIn(state, [transientItemID, key]);
      });
    })
    .addCase(ActionTypes.REPLACE_TRANSIENT_DATA, (state, action: any) => {
      const { transientItemID, data } = action.payload;
      Utils.deleteIn(state, transientItemID);
      Utils.setIn(state, transientItemID, data);
    })
    .addCase(ActionTypes.SET_SHARED_PROPERTY, (state, action: any) => {
      const { key, value } = action.payload;
      Utils.setIn(state, `${PegaConstants.SHARED}.${key}`, value);
    })
    .addCase(ActionTypes.ADD_SUGGESTION_CONTEXT, (state, action: any) => {
      const { context, data } = action.payload;
      return Utils.setIn(state, context, data);
    });
});

export default caseReducer;
